import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { SinatraState } from '@rsos/sinatra';
import normalizeError from '@rsos/sinatra/src/utils/normalizeError';
import {
  removeAllCalls,
  removeCall,
} from '../emergencyCalls/emergencyCallsSlice';
import { organizationSelector } from '../selectors/organizationSelector';
import {
  fetchLanguageList as apiFetchLanguageList,
  setConversationLanguage as apiSetConversationLanguage,
  fetchConversationData as apiFetchConversationData,
} from './chatsAPI';
import {
  ChatsState,
  ConversationData,
  ConversationState,
  TranslationLanguage,
} from './chatsTypes';

const initialState: ChatsState = {
  conversations: {},
  languages: [],
  loading: false,
  error: null,
};

const conversationDefaults: ConversationState = {
  isTranslationMenuOpen: false,
  isCollapsed: false,

  // No real conversation data available until we fetch it.
  serverTimeMillis: 0,
  conversationId: '',
  sessionId: '',
  authorityAccountId: '',
  authorityDisplayName: '',
  callerId: '',
  outboundTranslation: false,
  status: 'pending',
  sessionType: 'sms',
};

export const fetchLanguageList = createAsyncThunk<
  TranslationLanguage[],
  string,
  {
    rejectValue: { message: string; status: number };
    state: { sinatra: SinatraState };
  }
>('chats/fetchLanguageList', async (_, { rejectWithValue, getState }) => {
  try {
    const orgName = organizationSelector(getState())?.name;
    if (!orgName)
      return rejectWithValue({
        message: 'No organization to fetch from',
        status: 0,
      });
    const response = await apiFetchLanguageList(orgName);
    return response;
  } catch (error) {
    const { message } = normalizeError(error);
    const axiosError = error as AxiosError;
    return rejectWithValue({
      message: message || 'Failed to fetch messages',
      status: axiosError.response?.status || 500,
    });
  }
});

export const fetchConversationData = createAsyncThunk<
  ConversationData,
  string,
  {
    rejectValue: { message: string; status: number };
    state: { sinatra: SinatraState };
  }
>(
  'chats/fetchConversationData',
  async (conversationId, { rejectWithValue, getState }) => {
    try {
      const orgName = organizationSelector(getState())?.name;
      if (!orgName)
        return rejectWithValue({
          message: 'No organization to fetch from',
          status: 0,
        });
      const response = await apiFetchConversationData(orgName, conversationId);
      return response;
    } catch (error) {
      const { message } = normalizeError(error);
      const axiosError = error as AxiosError;
      return rejectWithValue({
        message: message || 'Failed to fetch messages',
        status: axiosError.response?.status || 500,
      });
    }
  },
);

export const setConversationLanguage = createAsyncThunk<
  void,
  {
    conversationId: string;
    selectedLanguageCode: string | null | undefined;
    outboundTranslation?: boolean;
  },
  {
    rejectValue: { message: string; status: number };
    state: { sinatra: SinatraState };
  }
>(
  'chats/setConversationLanguage',
  async (payload, { rejectWithValue, getState, dispatch }) => {
    try {
      const orgName = organizationSelector(getState())?.name;
      if (!orgName)
        return rejectWithValue({
          message: 'No organization to fetch from',
          status: 0,
        });
      dispatch(
        // This is a circular definition, but it's fine and on-purpose. There's
        // no way to call this function without the slice being defined.
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        chatsSlice.actions.optimisticUpdate({
          conversationId: payload.conversationId,
          // Translated into conversation fields. The payload's own key names are
          // request shape, so writing those updated nothing the UI reads and
          // the optimistic update did no optimising.
          update: {
            overrideCallerLanguage: payload.selectedLanguageCode ?? undefined,
            ...(payload.outboundTranslation === undefined
              ? {}
              : { outboundTranslation: payload.outboundTranslation }),
          },
        }),
      );
      await apiSetConversationLanguage(orgName, payload);
      await dispatch(fetchConversationData(payload.conversationId));
      return;
    } catch (error) {
      const { message } = normalizeError(error);
      const axiosError = error as AxiosError;
      return rejectWithValue({
        message: message || 'Failed to fetch messages',
        status: axiosError.response?.status || 500,
      });
    }
  },
);

const chatsSlice = createSlice({
  name: 'chats',
  initialState,
  reducers: {
    collapseChat: (
      state,
      action: PayloadAction<{
        conversationId: string;
        isCollapsed: boolean;
      }>,
    ) => {
      const { conversationId, isCollapsed } = action.payload;
      state.conversations[conversationId] = {
        ...conversationDefaults,
        ...state.conversations[conversationId],
        isCollapsed,
      };
    },
    expandAllChatsForCall: (
      state,
      action: PayloadAction<{
        callerId: string;
      }>,
    ) => {
      const { callerId } = action.payload;
      for (const convo of Object.values(state.conversations)) {
        if (convo.callerId === callerId) {
          convo.isCollapsed = false;
        }
      }
    },
    openChatTranslationMenu: (
      state,
      action: PayloadAction<{
        conversationId: string;
        isTranslationMenuOpen: boolean;
      }>,
    ) => {
      const { conversationId, isTranslationMenuOpen } = action.payload;
      state.conversations[conversationId] = {
        ...conversationDefaults,
        ...state.conversations[conversationId],
        isTranslationMenuOpen,
      };
    },
    optimisticUpdate: (
      state,
      action: PayloadAction<{
        conversationId: string;
        update: Partial<ConversationState>;
      }>,
    ) => {
      const { conversationId, update } = action.payload;
      state.conversations[conversationId] = {
        ...conversationDefaults,
        ...state.conversations[conversationId],
        ...update,
      };
    },
  },
  extraReducers: builder => {
    builder
      // Fetch languages
      .addCase(fetchLanguageList.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLanguageList.fulfilled, (state, action) => {
        state.loading = false;
        state.languages = action.payload;
      })
      .addCase(fetchLanguageList.rejected, (state, action) => {
        state.loading = false;
        state.error = {
          message: action.payload?.message || 'Failed to fetch messages',
          status: action.payload?.status || 500,
        };
      })

      // Fetch conversation
      .addCase(fetchConversationData.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversationData.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations[action.payload.conversationId] = {
          ...conversationDefaults,
          ...state.conversations[action.payload.conversationId],
          ...action.payload,
        };
      })
      .addCase(fetchConversationData.rejected, (state, action) => {
        state.loading = false;
        state.error = {
          message: action.payload?.message || 'Failed to fetch messages',
          status: action.payload?.status || 500,
        };
      })

      .addCase(removeCall, (state, action) => {
        for (const [id, convo] of Object.entries(state.conversations)) {
          if (convo.callerId === action.payload) {
            delete state.conversations[id];
          }
        }
      })
      .addCase(removeAllCalls, state => {
        state.conversations = {};
      });
  },
});

export const { openChatTranslationMenu, collapseChat, expandAllChatsForCall } =
  chatsSlice.actions;

const EMPTY_TRANSLATION_LIST: TranslationLanguage[] = [];
export function selectTranslationLanguages(state: { chats: ChatsState }) {
  return state.chats?.languages || EMPTY_TRANSLATION_LIST;
}

export function selectConversationState(conversationId?: string | null) {
  return (state: { chats: ChatsState }) => {
    if (!conversationId) {
      return conversationDefaults;
    }
    const conversation = state.chats.conversations[conversationId];
    if (!conversation) return conversationDefaults;
    return conversation;
  };
}

export function selectConversationsForCallerId(
  callerId: string | null | undefined,
) {
  return (state: { chats: ChatsState }) => {
    return Object.values(state.chats.conversations).filter(
      c => c.callerId == callerId,
    );
  };
}

export default chatsSlice.reducer;
